import { defaultSchema, type Schema } from 'hast-util-sanitize';
import type { PhrasingContent, Root, Text } from 'mdast';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import { SKIP, visit } from 'unist-util-visit';
import { markSecretsForDm } from '../secrets.js';
import { scanWikilinks } from '../wikilink.js';

/**
 * remark-Plugin: wandelt `[[Ziel#Abschnitt|Alias]]` in `<wiki-link>` und
 * `![[Ziel|300]]` in `<wiki-embed>` um. Code ist nicht betroffen, weil nur
 * Textknoten betrachtet werden.
 */
export function remarkWikilinks() {
  return (tree: Root) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || index === undefined || !node.value.includes('[[')) return;
      const occs = scanWikilinks(node.value, []);
      if (occs.length === 0) return;
      const parts: PhrasingContent[] = [];
      let last = 0;
      for (const o of occs) {
        if (o.start > last) parts.push({ type: 'text', value: node.value.slice(last, o.start) });
        const props: Record<string, string> = { dataTarget: o.target };
        if (o.heading) props.dataHeading = o.heading;
        if (o.alias !== null) props.dataAlias = o.alias;
        if (o.embed) {
          if (o.width !== null) props.dataWidth = String(o.width);
          parts.push({
            type: 'wikiEmbed',
            data: { hName: 'wiki-embed', hProperties: props },
            children: [],
          } as unknown as PhrasingContent);
        } else {
          const display = o.alias || (o.target ? o.target.split('/').pop()! : '') + (o.heading ? (o.target ? ' › ' : '') + o.heading : '');
          parts.push({
            type: 'wikiLink',
            data: { hName: 'wiki-link', hProperties: props },
            children: [{ type: 'text', value: display }],
          } as unknown as PhrasingContent);
        }
        last = o.end;
      }
      if (last < node.value.length) parts.push({ type: 'text', value: node.value.slice(last) });
      parent.children.splice(index, 1, ...parts);
      return [SKIP, index + parts.length];
    });
  };
}

const da = defaultSchema.attributes ?? {};

/** Sanitizer-Schema: GitHub-Standard plus eigene Elemente/Klassen. */
export const sanitizeSchema: Schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'wiki-link', 'wiki-embed', 'mark', 'u'],
  attributes: {
    ...da,
    'wiki-link': ['dataTarget', 'dataHeading', 'dataAlias'],
    'wiki-embed': ['dataTarget', 'dataHeading', 'dataAlias', 'dataWidth'],
    div: [...(da.div ?? []), ['className', 'dm-secret', 'dm-comment']],
    span: [...(da.span ?? []), ['className', 'dm-comment']],
    img: [...(da.img ?? []), 'width', 'height'],
  },
};

/** Bereitet den Body für die Anzeige auf (DM: Geheimnisse markieren). */
export function prepareForRender(body: string, isGameMaster: boolean): string {
  return isGameMaster ? markSecretsForDm(body) : body;
}

/**
 * Rendert Markdown zu sicherem HTML (für Tests und serverseitige Vorschauen).
 * Die SPA nutzt dieselben Plugins über react-markdown.
 */
export function renderMarkdownToHtml(body: string, opts: { isGameMaster: boolean }): string {
  const file = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkWikilinks)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehypeSanitize, sanitizeSchema)
    .use(rehypeStringify)
    .processSync(prepareForRender(body, opts.isGameMaster));
  return String(file);
}
